/**
 * TLP – Offer Champion
 * PDP subscription offer widget. Kids count -> plan (long / short) -> box
 * contents -> value stack -> multi-line add to cart.
 *
 * Pricing is derived, never typed. Retail comes from variant.price and the
 * subscription price from the Skio selling plan allocation (alloc.price);
 * every discount percentage shown is computed from those two numbers.
 *
 * Slots are generic: one anchor plus up to three add-ons, each carrying a role
 * ('optional', 'free_on_long', 'always'), so the same block drops onto any PDP
 * by pointing the slots at different products.
 *
 * Selling plan ids come from Liquid via variant.selling_plan_allocations (see
 * snippets/tlp-offer-champion.liquid). Plans are matched to a billing interval
 * by billing policy first, falling back to the plan-name regex used by
 * assets/cart-delivery-interval.js.
 */
(function () {
  "use strict";

  /* ---------------------------------------------------------------- utils */

  function money(cents) {
    var c = Math.round(Number(cents) || 0);
    if (
      typeof formatMoney === "function" &&
      window.theme &&
      window.theme.moneyFormat
    ) {
      return formatMoney(c, window.theme.moneyFormat);
    }
    return "$" + (c / 100).toFixed(2);
  }

  /** Fill {tokens} in merchant-editable copy. Unknown tokens are left alone. */
  function tpl(str, vars) {
    if (!str) return "";
    return String(str).replace(/\{(\w+)\}/g, function (m, key) {
      return Object.prototype.hasOwnProperty.call(vars, key)
        ? String(vars[key])
        : m;
    });
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function monthsFromBillingPolicy(alloc) {
    if (!alloc) return null;
    var ic = parseInt(alloc.billingIntervalCount, 10);
    if (isNaN(ic) || ic < 1 || ic > 12) return null;
    var iv = (alloc.billingInterval || "").toString().toLowerCase();
    if (iv && iv !== "month" && iv !== "months" && iv !== "monthly") return null;
    return ic;
  }

  /** Match Skio / Shopify names like "Every 1 Month", "Every 3 Months". */
  function inferMonthsFromPlanName(name) {
    if (!name) return null;
    var m = (name + "").match(/every\s*(\d+)\s*month/i);
    if (m) return parseInt(m[1], 10);
    m = (name + "").match(/\b(\d+)\s*[\s-]*months?\b/i);
    if (m) return parseInt(m[1], 10);
    return null;
  }

  function allocMonths(alloc) {
    var byPolicy = monthsFromBillingPolicy(alloc);
    if (byPolicy !== null) return byPolicy;
    return inferMonthsFromPlanName(alloc && alloc.name);
  }

  function firstSellableVariant(product) {
    if (!product || !product.variants || !product.variants.length) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) return product.variants[i];
    }
    return product.variants[0];
  }

  function findAlloc(variant, months) {
    if (!variant || !variant.allocations) return null;
    for (var i = 0; i < variant.allocations.length; i++) {
      if (allocMonths(variant.allocations[i]) === months) {
        return variant.allocations[i];
      }
    }
    return null;
  }

  function pct(retail, sub) {
    if (!retail || retail <= 0) return 0;
    return Math.round((1 - sub / retail) * 100);
  }

  /**
   * Quantity discount for a line, as a percentage off the unit price.
   *
   * The store applies a volume ladder per cart line on top of the Skio
   * subscription price — verified against cart/add.js: gummies on the 3-month
   * plan return $33.74 / $32.06 / $30.37 per unit at quantities 1 / 2 / 3,
   * i.e. 0%, 5% and 10% off the already-discounted price. The last step
   * extends to any higher quantity, matching the observed cap at 10%.
   *
   * Without this the widget quotes unit x qty and overstates every line where
   * quantity is 2 or more — which the 90-day plan always is.
   */
  function volumePct(tiers, qty) {
    if (!tiers || !tiers.length) return 0;
    var i = Math.min(Math.max(qty, 1), tiers.length) - 1;
    var v = Number(tiers[i]);
    return isNaN(v) ? 0 : v;
  }

  /**
   * Unit price after the line's volume tier, rounded the way the cart rounds.
   *
   * The discount amount is floored rather than the discounted price rounded.
   * Rounding the price gives $64.10 on two gummies where the cart charges
   * $64.12 — 3374 x 0.95 is 3205.3, and the cart bills 3206. Flooring the
   * discount (3374 - floor(168.7) = 3206) reproduces every quantity checked
   * against cart/add.js: 3374, 6412, 9111, 12148, 15185.
   */
  function linePrice(unitCents, qty, off) {
    if (!off) return unitCents * qty;
    return (unitCents - Math.floor((unitCents * off) / 100)) * qty;
  }

  /* ------------------------------------------------------------- instance */

  function OfferChampion(root) {
    this.root = root;
    this.mount = root.querySelector("[data-tlpoc-mount]");
    this.errorEl = root.querySelector("[data-tlpoc-error]");

    var id = root.getAttribute("data-section-id");
    var dataEl = document.querySelector(
      'script[data-tlpoc-data="' + (id || "").replace(/"/g, '\\"') + '"]'
    );
    if (!dataEl || !this.mount) return;

    try {
      this.cfg = JSON.parse(dataEl.textContent);
    } catch (e) {
      this.fail("tlp-offer-champion: bad block JSON", e);
      return;
    }

    // Liquid emits a trailing null so it never has to track the last comma.
    this.cfg.slots = (this.cfg.slots || []).filter(Boolean);
    this.cfg.timeline = (this.cfg.timeline || []).filter(Boolean);
    this.cfg.trust = (this.cfg.trust || []).filter(Boolean);

    // "0,5,10,15" -> [0,5,10,15]; index is total cart units - 1, last extends
    this.tiers = String(this.cfg.volumeTiers || "")
      .split(",")
      .map(function (x) { return Number(String(x).trim()); })
      .filter(function (x) { return !isNaN(x); });

    this.anchor = null;
    this.addons = [];
    for (var i = 0; i < this.cfg.slots.length; i++) {
      var s = this.cfg.slots[i];
      if (s.kind === "anchor") this.anchor = s;
      else this.addons.push(s);
    }

    if (!this.anchor || !firstSellableVariant(this.anchor.product)) {
      this.fail("tlp-offer-champion: anchor product missing or has no variants");
      return;
    }

    var picked = {};
    for (var j = 0; j < this.addons.length; j++) {
      picked[j] = !!this.addons[j].preselected;
    }

    this.state = { kids: 1, plan: "long", picked: picked, once: false, adding: false };
    this.render();
  }

  OfferChampion.prototype.fail = function (msg, err) {
    if (window.console) console.error(msg, err || "");
    if (this.mount) this.mount.hidden = true;
    if (this.errorEl) this.errorEl.hidden = false;
  };

  OfferChampion.prototype.plan = function (which) {
    return this.cfg.plans[which || this.state.plan];
  };

  /* --------------------------------------------------------- price model */

  /**
   * Live unit prices for a slot on a given plan. Subscription price comes from
   * the Skio allocation; if a plan has no allocation we fall back to retail so
   * the widget never invents a discount that Skio will not honour.
   */
  OfferChampion.prototype.unit = function (slot, planKey) {
    var variant = firstSellableVariant(slot.product);
    if (!variant) return null;

    var months = this.plan(planKey).months;
    /* A slot marked one-time is an upsell, not part of the subscription: it
       carries no selling plan and so is charged once, at retail. Quoting the
       subscription price here would under-quote what the cart then charges. */
    var alloc = slot.oneTime ? null : findAlloc(variant, months);
    var retail = variant.price;
    var sub = alloc && alloc.price != null ? alloc.price : retail;

    return {
      variantId: variant.id,
      variantTitle: variant.title,
      planId: alloc ? alloc.planId : null,
      hasPlan: !!alloc,
      retail: retail,
      sub: sub,
      savePct: pct(retail, sub),
      available: !!variant.available
    };
  };

  OfferChampion.prototype.isFree = function (slot) {
    return slot.role === "free_on_long" && this.state.plan === "long";
  };

  /**
   * A free_on_long slot is gifted (and locked) on the long plan, but off the
   * long plan it behaves exactly like an optional add-on the shopper can buy —
   * that paid state is what the upsell nudge is arguing against.
   */
  OfferChampion.prototype.isToggleable = function (slot) {
    if (slot.role === "optional") return true;
    return slot.role === "free_on_long" && this.state.plan !== "long";
  };

  OfferChampion.prototype.isIncluded = function (slot, idx) {
    if (slot.role === "always") return true;
    if (this.isFree(slot)) return true;
    return !!this.state.picked[idx];
  };

  /**
   * Units this plan puts in the cart for the anchor product. The ladder is
   * applied per cart line, so this is the quantity that earns the anchor's
   * tier — add-ons earn their own from their own quantities.
   */
  OfferChampion.prototype.unitsFor = function (planKey) {
    var s = this.state;
    if (s.once) return this.plan("short").anchorQty * s.kids;
    return this.plan(planKey).anchorQty * s.kids;
  };

  /** Everything the view and the cart payload need, derived from state. */
  OfferChampion.prototype.compute = function () {
    var s = this.state;
    var cfg = this.cfg;
    var self = this;
    var plan = this.plan();
    var isLong = s.plan === "long";

    var anchorUnit = this.unit(this.anchor, s.plan);
    var anchorQty = plan.anchorQty * s.kids;

    var units = this.unitsFor(s.plan);

    /*
     * A Buy X get Y consumes the anchor units as its qualifying items, and
     * Shopify then drops the volume discount from that line — verified in the
     * cart: three gummies bill $101.22 with the gift free, or $91.11 with the
     * gift charged, never both. When the gift is live the anchor therefore
     * earns no tier, unless the store is configured so the two do combine.
     */
    var giftActive = false;
    if (!s.once) {
      this.addons.forEach(function (slot, idx) {
        if (self.isFree(slot) && self.isIncluded(slot, idx) && self.unit(slot, s.plan)) {
          giftActive = true;
        }
      });
    }
    var suppressed = giftActive && cfg.giftSuppressesVolume;
    var tierPct = suppressed ? 0 : volumePct(this.tiers, anchorQty);

    var lines = [];
    var total = linePrice(anchorUnit.sub, anchorQty, tierPct);
    var retail = anchorUnit.retail * anchorQty;

    lines.push({
      slot: this.anchor,
      idx: -1,
      unit: anchorUnit,
      qty: anchorQty,
      lineTotal: linePrice(anchorUnit.sub, anchorQty, tierPct),
      included: true,
      free: false,
      toggleable: false
    });

    this.addons.forEach(function (slot, idx) {
      var u = self.unit(slot, s.plan);
      if (!u) return;
      var included = self.isIncluded(slot, idx);
      var free = self.isFree(slot);
      /* Per-kid only where the item is genuinely one-each: the free gift and
         anything always included. An optional add-on is an upsell — one per
         order, however many kids are being covered. */
      var perKid = slot.role === "free_on_long" || slot.role === "always";
      var qty = perKid ? slot.qtyPerKid * s.kids : slot.qtyPerKid;
      var lt = linePrice(u.sub, qty, volumePct(self.tiers, qty));

      if (included) {
        retail += u.retail * qty;
        if (!free) total += lt;
      }

      lines.push({
        slot: slot,
        idx: idx,
        unit: u,
        qty: qty,
        lineTotal: lt,
        included: included,
        free: free,
        toggleable: self.isToggleable(slot)
      });
    });

    if (cfg.showShippingRow && plan.freeShipping) retail += cfg.shippingValue;

    var shortPlan = this.plan("short");
    var longPlan = this.plan("long");
    var shortUnit = this.unit(this.anchor, "short");
    var cycles = shortPlan.months > 0 ? longPlan.months / shortPlan.months : 1;
    var shortQty = shortPlan.anchorQty * s.kids;
    var shortEquivalent =
      linePrice(shortUnit.sub, shortQty, volumePct(this.tiers, shortQty)) * cycles +
      (shortPlan.freeShipping ? 0 : cfg.shippingValue * cycles);

    var onceQty = shortPlan.anchorQty * s.kids;
    var onceTotal = linePrice(
      anchorUnit.retail,
      onceQty,
      volumePct(this.tiers, onceQty)
    );

    var payTotal = s.once ? onceTotal : total;
    var payRetail = s.once ? onceTotal : retail;
    var boxDays = plan.months * 30;
    var days = boxDays * s.kids;

    return {
      isLong: isLong,
      plan: plan,
      lines: lines,
      anchorUnit: anchorUnit,
      anchorQty: anchorQty,
      total: payTotal,
      retail: payRetail,
      save: Math.max(0, payRetail - payTotal),
      subTotal: total,
      subRetail: retail,
      subSave: Math.max(0, retail - total),
      onceTotal: onceTotal,
      // What the same window costs on the short plan, shipping included. The
      // long plan absorbs that shipping, which is where its advantage sits
      // once unit prices match.
      shortEquiv: shortEquivalent,
      shortSave: Math.max(0, shortEquivalent - payTotal),
      units: units,
      tierPct: tierPct,
      // Whole box per day: the billing window in days, not divided per child,
      // so the figure matches what actually leaves the account each day.
      perDay: boxDays > 0 ? payTotal / boxDays : 0,
      perDayPerKid: days > 0 ? payTotal / days : 0
    };
  };

  /** Token values available to every piece of merchant copy. */
  OfferChampion.prototype.vars = function (v, extra) {
    var s = this.state;
    var out = {
      kids: s.kids,
      kidword: s.kids === 1 ? this.cfg.copy.kidWordOne : this.cfg.copy.kidWordMany,
      months: v.plan.months,
      // Full window the offer is framed around, i.e. the long plan's length.
      // {months} is the current plan's interval and differs on the short card.
      span: this.cfg.plans.long.months,
      days: v.plan.months * 30,
      weeks: v.plan.months * 4,
      // Formatted shipping value, so a plan that charges for delivery can
      // quote the cost instead of the copy hardcoding a figure.
      shipping: money(this.cfg.shippingValue),
      shortequiv: money(v.shortEquiv),
      shortsave: money(v.shortSave),
      // The long plan by name, for copy that has to stay true on both cards —
      // "free on the {longplan}" reads correctly whichever plan is selected,
      // where {plan} would name the short plan and state the opposite.
      longplan: this.cfg.plans.long.title,
      qty: v.anchorQty,
      save: v.anchorUnit.savePct,
      total: money(v.total),
      value: money(v.retail),
      keep: money(v.save),
      subkeep: money(v.subSave),
      once: money(v.onceTotal),
      perday: money(v.perDay),
      perdayperkid: money(v.perDayPerKid),
      plan: v.plan.title
    };
    if (extra) {
      for (var k in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, k)) out[k] = extra[k];
      }
    }
    return out;
  };

  /* ---------------------------------------------------------------- view */

  OfferChampion.prototype.render = function () {
    var v = this.compute();
    var cfg = this.cfg;
    var c = cfg.copy;
    var s = this.state;
    var self = this;
    var V = this.vars(v);

    var frag = document.createDocumentFragment();

    var flow = el("div", "tlpoc-flow");

    /* 1. kids */
    var kidsCol = el("div", "tlpoc-flow__col");
    kidsCol.appendChild(this.stepHead(1, tpl(c.stepKids, V)));
    /*
     * Segmented pill control. The filled pill is a single positioned element
     * that slides between options rather than the active button changing
     * colour. render() rebuilds the DOM, so the pill is inserted at the
     * previous index and moved to the new one on the next frame — otherwise
     * the element would be born in its final position with nothing to
     * transition from.
     */
    var kidTrack = el("div", "tlpoc-kids");
    kidTrack.setAttribute("role", "group");
    kidTrack.style.gridTemplateColumns = "repeat(" + cfg.maxKids + ", 1fr)";
    kidTrack.style.setProperty("--tlpoc-kid-count", cfg.maxKids);

    var thumb = el("span", "tlpoc-kids__thumb");
    var fromIdx = (this._prevKids == null ? s.kids : this._prevKids) - 1;
    var toIdx = s.kids - 1;
    thumb.style.transform = "translateX(" + fromIdx * 100 + "%)";
    kidTrack.appendChild(thumb);

    for (var n = 1; n <= cfg.maxKids; n++) {
      (function (num) {
        var b = el("button", "tlpoc-kid" + (num === s.kids ? " is-on" : ""), String(num));
        b.type = "button";
        b.setAttribute("aria-pressed", num === s.kids ? "true" : "false");
        b.addEventListener("click", function () {
          self.set({ kids: num });
        });
        kidTrack.appendChild(b);
      })(n);
    }
    kidsCol.appendChild(kidTrack);

    if (fromIdx !== toIdx) {
      window.requestAnimationFrame(function () {
        thumb.style.transform = "translateX(" + toIdx * 100 + "%)";
      });
    }
    this._prevKids = s.kids;
    flow.appendChild(kidsCol);

    /* 2. plan */
    var planCol = el("div", "tlpoc-flow__col");
    planCol.appendChild(this.stepHead(2, tpl(c.stepPlan, V)));
    planCol.appendChild(this.planCard("long", v));
    planCol.appendChild(this.planCard("short", v));
    // Stays visible on both plans: switching to the long one should show the
    // remaining months filling in, not make the chart disappear.
    if (!s.once) {
      var tl = this.timelineCard(v);
      if (tl) planCol.appendChild(tl);
    }
    flow.appendChild(planCol);

    /* 3. box */
    var boxCol = el("div", "tlpoc-flow__col");
    boxCol.appendChild(this.stepHead(3, tpl(c.stepBox, V)));
    boxCol.appendChild(this.kitPanel(v));

    if (cfg.showOneTime) {
      var onceWrap = el("div", "tlpoc-once");
      var onceBtn = el(
        "button",
        "tlpoc-once__btn",
        tpl(s.once ? c.onceOn : c.onceOff, V)
      );
      onceBtn.type = "button";
      onceBtn.addEventListener("click", function () {
        /* "Buy once at full price" is a purchase, not a mode: switch the
           widget to the one-time line and send it to the cart in one go. */
        self.set({ once: true });
        self.addToCart();
      });
      onceWrap.appendChild(onceBtn);
      boxCol.appendChild(onceWrap);
    }
    flow.appendChild(boxCol);
    frag.appendChild(flow);

    frag.appendChild(this.footer(v));

    this.mount.innerHTML = "";
    this.mount.appendChild(frag);
  };

  /**
   * The kit: a dark panel holding the box contents as a grid of cards, with
   * the total value above and what is actually charged below. Each card
   * carries its own state — core, gifted, addable, or included — so the shape
   * of the box is readable at a glance.
   */
  OfferChampion.prototype.kitPanel = function (v) {
    var self = this;
    var cfg = this.cfg;
    var c = cfg.copy;
    var s = this.state;
    var V = this.vars(v);

    var kit = el("div", "tlpoc-kit");

    var head = el("div", "tlpoc-kit__head");
    var headL = el("div", "tlpoc-kit__headings");
    if (c.kitEyebrow) headL.appendChild(el("div", "tlpoc-kit__eyebrow", tpl(c.kitEyebrow, V)));
    headL.appendChild(el("div", "tlpoc-kit__title", tpl(c.kitTitle, V)));
    head.appendChild(headL);

    var headR = el("div", "tlpoc-kit__value");
    headR.appendChild(el("div", "tlpoc-kit__value-label", tpl(c.kitValueLabel, V)));
    headR.appendChild(el("div", "tlpoc-kit__value-amount", money(v.retail)));
    head.appendChild(headR);
    kit.appendChild(head);

    var grid = el("div", "tlpoc-kit__grid");
    v.lines.forEach(function (line) {
      grid.appendChild(self.itemCard(line, v));
    });
    if (cfg.showShippingRow) grid.appendChild(this.shipCard(v));
    kit.appendChild(grid);

    var foot = el("div", "tlpoc-kit__foot");
    var footL = el("div", "tlpoc-kit__foot-lines");
    footL.appendChild(
      el(
        "div",
        "tlpoc-kit__billing",
        tpl(s.once ? c.billOnce : v.isLong ? c.billLong : c.billShort, V)
      )
    );

    var names = [];
    v.lines.forEach(function (l) {
      if (l.idx === -1 || (l.included && !s.once)) names.push(l.slot.name);
    });
    footL.appendChild(
      el("div", "tlpoc-kit__contents", names.join(" + ") + " · " + V.kids + " " + V.kidword)
    );
    foot.appendChild(footL);

    /*
     * Per day rather than the total: the button below already carries the
     * total, and the smaller number is the one that argues for the box.
     */
    var footR = el("div", "tlpoc-kit__foot-total");
    footR.appendChild(el("div", "tlpoc-kit__total", tpl(c.kitPerDay, V)));
    if (!s.once) {
      footR.appendChild(el("div", "tlpoc-kit__keep", tpl(c.keepChip, V)));
    }
    foot.appendChild(footR);
    kit.appendChild(foot);

    return kit;
  };

  /** One product card inside the kit grid. */
  OfferChampion.prototype.itemCard = function (line, v) {
    var self = this;
    var c = this.cfg.copy;
    var s = this.state;
    var slot = line.slot;
    var u = line.unit;
    var isAnchor = line.idx === -1;

    var V = this.vars(v, {
      name: slot.name,
      qty: line.qty,
      price: money(line.lineTotal),
      value: money(u.retail * line.qty),
      save: u.savePct
    });

    var state = isAnchor
      ? "core"
      : line.free
        ? "gift"
        : line.included
          ? "added"
          : "add";

    var card = el(line.toggleable ? "button" : "div", "tlpoc-item is-" + state);
    if (line.toggleable) {
      card.type = "button";
      card.setAttribute("aria-pressed", line.included ? "true" : "false");
      card.addEventListener("click", function () {
        var picked = {};
        for (var k in s.picked) picked[k] = s.picked[k];
        picked[line.idx] = !picked[line.idx];
        self.set({ picked: picked, once: false });
      });
    }

    var top = el("div", "tlpoc-item__top");
    var thumb = el("div", "tlpoc-item__thumb");
    if (slot.product.featuredImage) {
      var img = document.createElement("img");
      img.src = slot.product.featuredImage;
      img.alt = "";
      img.loading = "lazy";
      thumb.appendChild(img);
    }
    top.appendChild(thumb);

    var tagText = isAnchor
      ? slot.tag || c.tagCore
      : line.free
        ? c.tagFreeGift
        : line.included
          ? c.tagAdded
          : c.tagOptional;
    if (tagText) top.appendChild(el("div", "tlpoc-item__tag", tpl(tagText, V)));
    card.appendChild(top);

    var name = el("div", "tlpoc-item__name");
    name.appendChild(document.createTextNode(slot.name + " "));
    name.appendChild(el("span", "tlpoc-item__qty", "×" + line.qty));
    card.appendChild(name);

    if (slot.desc) card.appendChild(el("div", "tlpoc-item__desc", tpl(slot.desc, V)));

    var prices = el("div", "tlpoc-item__prices");
    prices.appendChild(
      el(
        "div",
        "tlpoc-item__price",
        line.free
          ? tpl(c.itemIncluded, V)
          : (isAnchor ? "" : "+ ") + money(line.lineTotal)
      )
    );
    /* Only strike a price there is actually a saving against — a one-time add-on
       sells at retail, so showing "$54.99 $54.99" would be noise. */
    var retailTotal = u.retail * line.qty;
    if (line.free || retailTotal > line.lineTotal) {
      prices.appendChild(el("div", "tlpoc-item__retail", money(retailTotal)));
    }
    card.appendChild(prices);

    return card;
  };

  /** Delivery card — no product behind it, so it carries an icon. */
  OfferChampion.prototype.shipCard = function (v) {
    var c = this.cfg.copy;
    var free = !!v.plan.freeShipping;
    var V = this.vars(v, { value: money(this.cfg.shippingValue) });

    var card = el("div", "tlpoc-item is-" + (free ? "gift" : "plain"));

    var top = el("div", "tlpoc-item__top");
    var thumb = el("div", "tlpoc-item__thumb tlpoc-item__thumb--icon");
    thumb.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M12 3l7 3v5.5c0 4.2-2.9 7.6-7 8.5-4.1-.9-7-4.3-7-8.5V6l7-3z" ' +
      'stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>' +
      '<path d="M9 12.2l2.1 2.1L15.2 10" stroke="currentColor" stroke-width="1.8" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';
    top.appendChild(thumb);
    top.appendChild(
      el("div", "tlpoc-item__tag", tpl(free ? c.tagFree : c.tagNotIncluded, V))
    );
    card.appendChild(top);

    card.appendChild(
      el("div", "tlpoc-item__name", tpl(free ? c.shipRowName : c.shipRowNamePaid, V))
    );
    card.appendChild(
      el("div", "tlpoc-item__desc", tpl(free ? c.shipFreeDesc : c.shipPaidDesc, V))
    );

    var prices = el("div", "tlpoc-item__prices");
    /* paid delivery is a cost, so it is priced like one: "+ $6.95", not a dash */
    prices.appendChild(
      el(
        "div",
        "tlpoc-item__price",
        free ? tpl(c.tagFree, V) : "+ " + money(this.cfg.shippingValue)
      )
    );
    if (free) {
      prices.appendChild(el("div", "tlpoc-item__retail", money(this.cfg.shippingValue)));
    }
    card.appendChild(prices);
    return card;
  };

  /**
   * The consistency argument. Shown directly under the short plan, and only
   * while the short plan is selected: at that point the shopper is looking at
   * a box that covers the first stretch of a longer window, so the timeline
   * shows what the remaining months would have done and offers the swap.
   */
  OfferChampion.prototype.timelineCard = function (v) {
    var cfg = this.cfg;
    var c = cfg.copy;
    var self = this;
    var V = this.vars(v);

    if (!cfg.timeline.length) return null;

    var card = el("div", "tlpoc-card tlpoc-card--inline");
    card.appendChild(el("div", "tlpoc-card__stripe"));
    if (c.eyebrow) card.appendChild(el("div", "tlpoc-card__eyebrow", tpl(c.eyebrow, V)));
    if (c.headline) card.appendChild(el("div", "tlpoc-card__headline", tpl(c.headline, V)));

    var steps = el("div", "tlpoc-steps");
    steps.style.gridTemplateColumns = "repeat(" + cfg.timeline.length + ", 1fr)";
    /*
     * Driven by how many units of the anchor are being bought rather than the
     * billing interval, so two boxes light two steps whichever plan they came
     * from. Capped at the number of steps there are.
     */
    var covered = Math.min(
      cfg.timeline.length,
      Math.max(1, v.anchorQty)
    );
    cfg.timeline.forEach(function (st, i) {
      var on = i < covered;
      var stepEl = el("div", "tlpoc-step" + (on ? " is-on" : ""));
      var head = el("div", "tlpoc-step__head");
      head.appendChild(el("div", "tlpoc-step__dot", String(i + 1)));
      head.appendChild(el("div", "tlpoc-step__label", tpl(st.label, V)));
      stepEl.appendChild(head);
      stepEl.appendChild(el("div", "tlpoc-step__title", tpl(st.title, V)));
      stepEl.appendChild(el("div", "tlpoc-step__desc", tpl(st.desc, V)));
      steps.appendChild(stepEl);
    });
    card.appendChild(steps);

    /*
     * Growth chart. A flat progress bar states the shortfall; bars that climb
     * month by month let a parent see it. Covered months fill solid and
     * animate up on render; the months this plan misses stay as dashed
     * outlines at the height they would have reached.
     */
    var chart = el("div", "tlpoc-growth");
    chart.appendChild(el("div", "tlpoc-growth__target"));
    /*
     * Only the months that just became covered animate. render() rebuilds the
     * DOM, so without this every bar would replay on each state change and the
     * fill would read as a reset rather than months being added.
     */
    var wasCovered = this._prevCovered == null ? covered : this._prevCovered;

    var cols = el("div", "tlpoc-growth__cols");
    cfg.timeline.forEach(function (st, i) {
      var on = i < covered;
      var isNew = on && i >= wasCovered;
      var col = el("div", "tlpoc-growth__col" + (on ? " is-on" : ""));
      var track = el("div", "tlpoc-growth__track");
      var bar = el("div", "tlpoc-growth__bar" + (isNew ? " is-new" : ""));
      // Cumulative, so each month visibly builds on the one before it.
      bar.style.setProperty(
        "--tlpoc-h",
        Math.round(((i + 1) / cfg.timeline.length) * 100) + "%"
      );
      bar.style.setProperty(
        "--tlpoc-delay",
        Math.max(0, i - wasCovered) * 150 + "ms"
      );
      track.appendChild(bar);
      col.appendChild(track);
      col.appendChild(el("div", "tlpoc-growth__tick", String(i + 1)));
      cols.appendChild(col);
    });
    chart.appendChild(cols);

    var bar = el("div", "tlpoc-cover");
    bar.appendChild(chart);

    this._prevCovered = covered;

    var coverRow = el("div", "tlpoc-cover__row");
    coverRow.appendChild(
      el(
        "div",
        "tlpoc-cover__line" + (v.isLong ? " is-good" : ""),
        tpl(v.isLong ? c.coverLineLong : c.coverLineShort, V)
      )
    );
    // the way out of a one-month supply: the button that switches the choice
    // to 90 days, which is the whole point of showing what month 1 misses
    if (!v.isLong && c.coverSwitch) {
      var sw = el("button", "tlpoc-cover__switch", tpl(c.coverSwitch, V));
      sw.type = "button";
      sw.addEventListener("click", function () {
        self.set({ plan: "long", once: false });
      });
      coverRow.appendChild(sw);
    }
    bar.appendChild(coverRow);
    card.appendChild(bar);
    return card;
  };

  /**
   * Step heading with an inline numbered badge. Previously the numbers lived
   * in a left rail column, which inset every card by the rail width; inline
   * lets the cards run the full width of the block.
   */
  OfferChampion.prototype.stepHead = function (num, text) {
    var head = el("div", "tlpoc-stephead");
    head.appendChild(el("span", "tlpoc-stephead__num", String(num)));
    head.appendChild(el("span", "tlpoc-stephead__label", text));
    return head;
  };

  OfferChampion.prototype.planCard = function (which, v) {
    var self = this;
    var c = this.cfg.copy;
    var s = this.state;
    var p = this.plan(which);
    var isLong = which === "long";
    var on = s.plan === which && !s.once;

    // Price this card on its own plan, not the active one.
    var u = this.unit(this.anchor, which);
    /*
     * Priced through unitsFor(which): the rate shown is the one this plan
     * would actually bill, including the tier its full basket earns.
     */
    var planQty = p.anchorQty * s.kids;
    var giftOnThisPlan =
      which === "long" &&
      !s.once &&
      !!this.giftSlot() &&
      !!this.unit(this.giftSlot(), which);
    var planTier =
      giftOnThisPlan && this.cfg.giftSuppressesVolume
        ? 0
        : volumePct(this.tiers, planQty);
    var planLine = linePrice(u.sub, planQty, planTier);
    var perMonthNow = planLine / (p.months * s.kids);
    var perMonthWas = (u.retail * p.anchorQty) / p.months;

    var V = this.vars(v, {
      save: u.savePct,
      months: p.months,
      weeks: p.months * 4,
      plan: p.title
    });

    var b = el(
      "button",
      "tlpoc-plan tlpoc-plan--" + (isLong ? "90" : "30") + (on ? " is-on" : "")
    );
    b.type = "button";
    b.setAttribute("aria-pressed", on ? "true" : "false");

    if (p.badge) b.appendChild(el("div", "tlpoc-plan__badge", tpl(p.badge, V)));

    var top = el("div", "tlpoc-plan__top");
    top.appendChild(el("div", "tlpoc-plan__radio"));
    var titles = el("div", "tlpoc-plan__titles");
    titles.appendChild(el("div", "tlpoc-plan__title", tpl(p.title, V)));


    if (isLong && c.socialProof) {
      titles.appendChild(el("div", "tlpoc-plan__proof", "★★★★★ " + tpl(c.socialProof, V)));
    }
    top.appendChild(titles);
    b.appendChild(top);

    // The gift was named on the card while the product being bought was not,
    // so the quantity the plan ships is stated first.
    var qtyLine = el(
      "div",
      "tlpoc-plan__qty",
      tpl(c.planQtyLabel, this.vars(v, { qty: planQty, name: this.anchor.name }))
    );
    b.appendChild(qtyLine);

    var mid = el("div", "tlpoc-plan__mid");

    // Free-gift tile: shows the gift product with its real retail struck out.
    if (isLong) {
      var chips = el("div", "tlpoc-plan__chips");
      var gift = this.giftSlot();
      if (gift) {
        var gu = this.unit(gift, "long");
        if (gu) {
          var giftQty = gift.qtyPerKid * s.kids;
          var giftValue = money(gu.retail * giftQty);
          var giftVars = this.vars(v, {
            name: gift.name,
            qty: giftQty,
            value: giftValue
          });

          var tile = el("div", "tlpoc-gift");
          var gthumb = el("div", "tlpoc-gift__thumb");
          if (gift.product.featuredImage) {
            var gimg = document.createElement("img");
            gimg.src = gift.product.featuredImage;
            gimg.alt = "";
            gimg.loading = "lazy";
            gthumb.appendChild(gimg);
          }
          tile.appendChild(gthumb);
          tile.appendChild(el("div", "tlpoc-gift__price", giftValue));
          tile.appendChild(el("div", "tlpoc-gift__label", tpl(c.freeTile, giftVars)));
          chips.appendChild(tile);
        }
      }
      /*
       * With no volume ladder both plans share a unit price, so the long plan
       * needs a true comparison rather than a cheaper number: what the same
       * window costs bought monthly, shipping included.
       */
      if (p.compareLabel) {
        chips.appendChild(
          el("div", "tlpoc-plan__compare", tpl(p.compareLabel, V))
        );
      }
      if (chips.children.length) mid.appendChild(chips);
    }

    var prices = el("div", "tlpoc-plan__prices");
    var row = el("div", "tlpoc-plan__price-row");
    row.appendChild(el("div", "tlpoc-plan__price", money(perMonthNow)));
    if (perMonthWas > perMonthNow) {
      row.appendChild(el("div", "tlpoc-plan__was", money(perMonthWas)));
    }
    prices.appendChild(row);
    prices.appendChild(el("div", "tlpoc-plan__per", c.perLabel || "/mo per kid"));
    mid.appendChild(prices);
    b.appendChild(mid);

    var foot = el("div", "tlpoc-plan__foot");
    foot.appendChild(el("div", "tlpoc-plan__save", tpl(p.saveLabel, V)));
    if (p.shipsLabel) {
      // Free delivery is an argument for the longer plan, so it is marked up
      // rather than left as another grey line.
      foot.appendChild(
        el(
          "div",
          "tlpoc-plan__ships" + (p.freeShipping ? " is-free" : ""),
          tpl(p.shipsLabel, V)
        )
      );
    }
    b.appendChild(foot);

    b.addEventListener("click", function () {
      self.set({ plan: which, once: false });
    });
    return b;
  };

  /** The add-on configured as the free gift on the long plan, if any. */
  OfferChampion.prototype.giftSlot = function () {
    for (var i = 0; i < this.addons.length; i++) {
      if (this.addons[i].role === "free_on_long") return this.addons[i];
    }
    return null;
  };

  OfferChampion.prototype.rowEl = function (line, v) {
    var self = this;
    var c = this.cfg.copy;
    var s = this.state;
    var slot = line.slot;
    var u = line.unit;
    var isAnchor = line.idx === -1;

    var V = this.vars(v, {
      name: slot.name,
      qty: line.qty,
      price: money(u.sub * line.qty),
      value: money(u.retail * line.qty),
      save: u.savePct
    });

    var state = isAnchor
      ? "plain"
      : line.free
        ? "locked"
        : line.included
          ? "added"
          : "toggle";

    var wrap = el("div", "tlpoc-row is-" + state);
    var main = el("div", "tlpoc-row__main");

    var thumb = el("div", "tlpoc-row__thumb");
    if (slot.product.featuredImage) {
      var img = document.createElement("img");
      img.src = slot.product.featuredImage;
      img.alt = "";
      img.loading = "lazy";
      img.width = 46;
      img.height = 46;
      thumb.appendChild(img);
    }
    main.appendChild(thumb);

    /*
     * Benefit-led hierarchy: the outcome the shopper is buying leads the row
     * and the product that delivers it sits underneath as supporting detail.
     */
    var body = el("div", "tlpoc-row__body");
    var head = el("div", "tlpoc-row__head");
    head.appendChild(
      el("div", "tlpoc-row__benefit", tpl(slot.desc || slot.name, V))
    );

    var tagText = isAnchor
      ? slot.tag || c.tagCore
      : line.free
        ? c.tagFree
        : line.included
          ? c.tagAdded
          : c.tagOptional;
    var tagKind = isAnchor
      ? "core"
      : line.free
        ? "free"
        : line.included
          ? "added"
          : "opt";
    if (tagText) head.appendChild(el("div", "tlpoc-row__tag is-" + tagKind, tpl(tagText, V)));
    body.appendChild(head);

    if (slot.desc) {
      body.appendChild(
        el("div", "tlpoc-row__product", slot.name + " ×" + line.qty)
      );
    }
    main.appendChild(body);

    // Price and its action live together on the right, so the cost of adding
    // something sits next to the control that adds it.
    var side = el("div", "tlpoc-row__side");
    var prices = el("div", "tlpoc-row__prices");
    prices.appendChild(
      el(
        "div",
        "tlpoc-row__price",
        line.free ? tpl(c.tagFree, V) : (isAnchor ? "" : "+ ") + money(u.sub * line.qty)
      )
    );
    prices.appendChild(el("div", "tlpoc-row__retail", money(u.retail * line.qty)));
    side.appendChild(prices);

    if (line.toggleable) {
      var btn = el(
        "button",
        "tlpoc-row__btn" + (line.included ? " is-on" : ""),
        tpl(line.included ? c.btnAdded : c.btnAdd, V)
      );
      btn.type = "button";
      btn.addEventListener("click", function () {
        var picked = {};
        for (var k in s.picked) picked[k] = s.picked[k];
        picked[line.idx] = !picked[line.idx];
        self.set({ picked: picked, once: false });
      });
      side.appendChild(btn);
    } else if (line.free) {
      side.appendChild(el("div", "tlpoc-row__check", "✓"));
    }
    main.appendChild(side);

    wrap.appendChild(main);

    // Nudge an optional slot that would be free on the long plan.
    if (slot.role === "free_on_long" && !v.isLong && c.freeUpsellOff) {
      var up = el("div", "tlpoc-row__upsell");
      up.appendChild(el("div", "tlpoc-row__upsell-text", tpl(c.freeUpsellOff, V)));
      if (c.freeUpsellBtn) {
        var ub = el("button", "tlpoc-row__upsell-btn", tpl(c.freeUpsellBtn, V));
        ub.type = "button";
        ub.addEventListener("click", function () {
          self.set({ plan: "long", once: false });
        });
        up.appendChild(ub);
      }
      wrap.appendChild(up);
    } else if (line.free && c.freeUpsellOn) {
      var up2 = el("div", "tlpoc-row__upsell");
      up2.appendChild(el("div", "tlpoc-row__upsell-text", tpl(c.freeUpsellOn, V)));
      wrap.appendChild(up2);
    }

    return wrap;
  };

  OfferChampion.prototype.shipRow = function (v) {
    var c = this.cfg.copy;
    var free = !!v.plan.freeShipping;
    var V = this.vars(v, { value: money(this.cfg.shippingValue) });

    var wrap = el("div", "tlpoc-row is-" + (free ? "locked" : "plain"));
    var main = el("div", "tlpoc-row__main");

    // Shield icon — delivery has no product image to show.
    var iconWrap = el("div", "tlpoc-row__thumb tlpoc-row__thumb--icon");
    iconWrap.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M12 3l7 3v5.5c0 4.2-2.9 7.6-7 8.5-4.1-.9-7-4.3-7-8.5V6l7-3z" ' +
      'stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>' +
      '<path d="M9 12.2l2.1 2.1L15.2 10" stroke="currentColor" stroke-width="1.8" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';
    main.appendChild(iconWrap);

    var body = el("div", "tlpoc-row__body");
    var head = el("div", "tlpoc-row__head");
    // The free name carries "FREE" in it, so it must not be reused when the
    // shopper is on a plan that does not include shipping.
    head.appendChild(
      el("div", "tlpoc-row__benefit", tpl(free ? c.shipRowName : c.shipRowNamePaid, V))
    );
    // When free, the row name already carries the offer and the green tick
    // signals inclusion — a "FREE" tag on top of that just repeats itself.
    if (!free) {
      head.appendChild(
        el("div", "tlpoc-row__tag is-opt", tpl(c.tagNotIncluded, V))
      );
    }
    body.appendChild(head);
    body.appendChild(
      el("div", "tlpoc-row__product", tpl(free ? c.shipFreeDesc : c.shipPaidDesc, V))
    );
    main.appendChild(body);

    var side = el("div", "tlpoc-row__side");
    var prices = el("div", "tlpoc-row__prices");
    prices.appendChild(el("div", "tlpoc-row__price", free ? tpl(c.tagFree, V) : "—"));
    if (free) prices.appendChild(el("div", "tlpoc-row__retail", money(this.cfg.shippingValue)));
    side.appendChild(prices);
    if (free) side.appendChild(el("div", "tlpoc-row__check", "✓"));
    main.appendChild(side);

    wrap.appendChild(main);
    return wrap;
  };

  OfferChampion.prototype.stackRow = function (label, value, mod) {
    var row = el("div", "tlpoc-stack__row " + (mod || ""));
    row.appendChild(el("div", "tlpoc-stack__label", label));
    row.appendChild(el("div", "tlpoc-stack__value", value));
    return row;
  };

  OfferChampion.prototype.footer = function (v) {
    var self = this;
    var cfg = this.cfg;
    var c = cfg.copy;
    var s = this.state;
    var V = this.vars(v);

    var foot = el("div", "tlpoc-foot");

    // Basket line and totals now live in the kit footer, so the sticky bar
    // carries only the action.

    // Reuse the theme's own button so radius, shadow and colours match.
    // Single line only — the qualifier sits under the button as a caption.
    var cta = el("button", "btn tlpoc-cta");
    cta.type = "button";
    cta.disabled = !!s.adding;
    cta.textContent = s.adding
      ? (window.theme && window.theme.strings && window.theme.strings.addingToCart) ||
        "Adding…"
      : tpl(c.ctaLabel, V);
    cta.addEventListener("click", function () {
      self.addToCart();
    });
    foot.appendChild(cta);

    /*
     * The sub-note said "pause or cancel anytime" directly above a trust mark
     * reading "Cancel anytime", and the kit footer already states the billing
     * cadence — so it only survives for the one-time case, where it says
     * something nothing else does.
     */
    if (s.once && c.ctaSubOnce) {
      foot.appendChild(el("div", "tlpoc-cta__note", tpl(c.ctaSubOnce, V)));
    }

    return foot;
  };

  /* ---------------------------------------------------------------- state */

  OfferChampion.prototype.set = function (patch) {
    for (var k in patch) {
      if (Object.prototype.hasOwnProperty.call(patch, k)) this.state[k] = patch[k];
    }
    this.render();
  };

  /* ----------------------------------------------------------------- cart */

  /**
   * Multi-line payload. Each line carries its own Skio selling plan id; a
   * one-time purchase omits selling_plan entirely.
   *
   * A free gift only becomes its own cart line under the 'zero_line' method,
   * which assumes a 100%-off Skio plan exists for it. Under 'bundled' the gift
   * cost is already inside the anchor plan price, so no line is sent.
   */
  OfferChampion.prototype.buildItems = function (v) {
    var s = this.state;
    var cfg = this.cfg;
    var items = [];

    function line(unit, qty) {
      var l = {
        id: unit.variantId,
        quantity: qty,
        // Marks the line as part of a configured offer. cart-items.liquid
        // renders these without a quantity stepper: the offer decides the
        // quantities, so editing one line would break the arithmetic the
        // widget quoted.
        properties: { _tlpoc_offer: "1" }
      };
      if (!s.once && unit.planId != null) {
        var num = parseInt(unit.planId, 10);
        l.selling_plan = isNaN(num) ? unit.planId : num;
      }
      return l;
    }

    if (s.once) {
      return [
        {
          id: v.anchorUnit.variantId,
          quantity: this.plan("short").anchorQty * s.kids,
          properties: { _tlpoc_offer: "1" }
        }
      ];
    }

    v.lines.forEach(function (l) {
      if (!l.included) return;
      if (l.free && cfg.freeGiftMethod === "bundled") return;
      if (l.free) {
        /*
         * Preferred route: send a zero-priced variant, so the line is free
         * because it costs nothing rather than because a discount matched it.
         * Nothing has to combine and nothing can out-rank it.
         *
         * Falling back to the real variant means the line depends on a Buy X
         * get Y discount, which Shopify only applies when it is not competing
         * with another automatic discount on the same cart. Either way the
         * gift goes one-time: Buy X get Y will not match a subscription line.
         */
        /*
         * Sent as the real variant on purpose. A zero-priced variant would be
         * free per unit with nothing to cap it, so raising the quantity would
         * hand over stock; the Buy X get Y discount frees only the quantity
         * its "maximum uses per order" allows and charges the rest.
         *
         * A free-gift variant can still be configured per slot, but it carries
         * that risk and is left unset by default.
         */
        var freeId = parseInt(l.slot.freeVariantId, 10);
        items.push({
          id: isNaN(freeId) ? l.unit.variantId : freeId,
          quantity: l.qty,
          // _tlpoc_gift additionally suppresses the remove control, so a
          // zero-priced line cannot be kept while its anchor is dropped.
          properties: { _tlpoc_offer: "1", _tlpoc_gift: "1" }
        });
        return;
      }
      items.push(line(l.unit, l.qty));
    });

    return items;
  };

  OfferChampion.prototype.addToCart = function () {
    if (this.state.adding) return;
    var self = this;
    var cfg = this.cfg;
    var items = this.buildItems(this.compute());
    if (!items.length) return;

    this.set({ adding: true });

    var fetchCfg =
      typeof fetchConfigDefaults === "function"
        ? fetchConfigDefaults("application/javascript")
        : { method: "POST", headers: {} };
    fetchCfg.method = "POST";
    fetchCfg.headers["Content-Type"] = "application/json";
    fetchCfg.body = JSON.stringify({ items: items });

    fetch(window.theme.routes.cart_add_url, fetchCfg)
      .then(function (res) {
        return res.json().then(function (data) {
          return { ok: res.ok, data: data };
        });
      })
      .then(function (out) {
        if (!out.ok || (out.data && out.data.status)) {
          var msg =
            (out.data && (out.data.description || out.data.message)) ||
            (window.theme.strings && window.theme.strings.cartAddError) ||
            "Could not add to cart.";
          window.alert(msg);
          self.set({ adding: false });
          return;
        }
        if (cfg.skipCart) {
          // Checkout does not expose quantity controls at all, so going
          // straight there removes the last place the offer could be edited.
          window.location.href = "/checkout";
          return;
        }
        self.afterAdd(out.data);
      })
      .catch(function (err) {
        if (window.console) console.error("tlp-offer-champion: add failed", err);
        window.alert(
          (window.theme.strings && window.theme.strings.cartAddError) ||
            "Could not add to cart."
        );
        self.set({ adding: false });
      });
  };

  /** Refresh the theme's cart sections, matching assets/bundle-builder.js. */
  OfferChampion.prototype.afterAdd = function (data) {
    var self = this;
    var path =
      typeof window.location.pathname === "string" ? window.location.pathname : "/";
    var sep = path.indexOf("?") >= 0 ? "&" : "?";
    var secUrl =
      path + sep + "sections=cart-items,cart-footer,cart-item-count,cart-live-region";

    fetch(secUrl, { credentials: "same-origin" })
      .then(function (r) {
        return r.json();
      })
      .then(function (sections) {
        document.body.dispatchEvent(
          new CustomEvent("shapes:cart:afteradditem", {
            bubbles: true,
            detail: {
              response: Object.assign({}, data, { sections: sections }),
              sourceId: "tlp-offer-champion",
              bundle: true
            }
          })
        );
        self.set({ adding: false });
      })
      .catch(function () {
        window.location.href = window.theme.routes.cart_url;
      });
  };

  /* ----------------------------------------------------------------- boot */

  function init(scope) {
    var roots = (scope || document).querySelectorAll("[data-tlpoc-root]");
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__tlpoc) continue;
      roots[i].__tlpoc = new OfferChampion(roots[i]);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", function (e) {
    init(e.target);
  });
})();
